<template>
  <subscription-guard>
    <div class="up-wrap">

      <!-- Page header -->
      <div class="up-header">
        <div>
          <h1 class="up-title">Upfront</h1>
          <p class="up-subtitle">Pay influencers a fixed fee — on top of the performance-based deal.</p>
        </div>
        <div class="up-header-right">
          <span class="up-vat-note">Amounts are excl. VAT</span>
          <v-btn outlined small class="up-btn-secondary" @click="dialog = true">
            <i class="fal fa-hand-holding-usd" style="margin-right:6px;font-size:12px"></i>
            Upfront payment
          </v-btn>
          <v-btn color="#e05540" dark small @click="newCampaign" :loading="creatingCampaign">
            <i class="fal fa-plus" style="margin-right:6px;font-size:12px"></i>
            Create campaign
          </v-btn>
        </div>
      </div>

      <!-- Info cards -->
      <div class="up-info-grid">
        <div class="up-info-card">
          <div class="up-info-icon"><i class="fal fa-question-circle"></i></div>
          <div class="up-info-title">What is an upfront payment?</div>
          <div class="up-info-text">A fixed amount you offer an influencer on top of the performance-based deal — to attract larger names, increase commitment, or require specific content like UGC.</div>
        </div>
        <div class="up-info-card">
          <div class="up-info-icon"><i class="fal fa-bullseye"></i></div>
          <div class="up-info-title">When does it make sense?</div>
          <div class="up-info-text">When you want to work with a specific influencer, have higher content requirements (e.g. UGC for ads), or want extra quality and commitment in the collaboration.</div>
        </div>
        <div class="up-info-card">
          <div class="up-info-icon"><i class="fal fa-shield-check"></i></div>
          <div class="up-info-title">Rules & responsibility</div>
          <div class="up-info-text">All amounts are excl. VAT. Upfront is binding once the influencer accepts. Make Influence is not a party to the deal — it's between you and the influencer.</div>
        </div>
        <div class="up-info-card">
          <div class="up-info-icon"><i class="fal fa-list-check"></i></div>
          <div class="up-info-title">Recommended approach</div>
          <div class="up-info-text">Agree on terms clearly. Get it in writing via Conversations and have the influencer accept. Only pay out after the influencer has accepted and all extra requirements are approved.</div>
        </div>
        <div class="up-info-card">
          <div class="up-info-icon"><i class="fal fa-sparkles"></i></div>
          <div class="up-info-title">Examples of extra requirements</div>
          <div class="up-info-text">Delivery of content for ads (UGC), a specific posting date, a set number of stories/posts, or rights to use the content commercially.</div>
        </div>
        <div class="up-info-card">
          <div class="up-info-icon"><i class="fal fa-info-circle"></i></div>
          <div class="up-info-title">Important to understand</div>
          <div class="up-info-text">An upfront payment is not a guarantee of performance. It is a payment for securing the collaboration and/or getting specific content delivered.</div>
        </div>
      </div>

      <!-- Payments table -->
      <div class="up-table-card">
        <div class="up-table-header">
          <div class="up-table-title">Your upfront payments</div>
          <v-btn small outlined class="up-btn-secondary" :loading="loading" @click="loadHistory">
            <i class="fal fa-sync" style="margin-right:6px;font-size:11px"></i> Refresh
          </v-btn>
        </div>

        <div v-if="loading && transactions.length === 0" class="up-loading">
          <v-progress-circular indeterminate color="#e05540" size="28"></v-progress-circular>
        </div>

        <div v-else class="up-table-scroll">
          <table class="up-table">
          <thead>
            <tr>
              <th>INFLUENCER</th>
              <th>CAMPAIGN</th>
              <th>AMOUNT</th>
              <th>STATUS</th>
              <th>DATE</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in transactions" :key="item.id" class="up-row">
              <td>
                <div class="up-inf-cell">
                  <div class="up-avatar" :style="{ background: avatarColor(item) }">
                    {{ avatarInitials(item) }}
                  </div>
                  <div>
                    <div class="up-inf-name">{{ item.influencer && item.influencer.user ? item.influencer.user.full_name : '—' }}</div>
                    <div class="up-inf-sub" v-if="item.reference || item.note">
                      {{ item.reference }}<template v-if="item.reference && item.note"> · </template>{{ item.note }}
                    </div>
                  </div>
                </div>
              </td>
              <td><div class="up-cell-val">{{ item.campaign ? item.campaign.name : '—' }}</div></td>
              <td><div class="up-amount">{{ (item.amount / 100) | numeralFormat('0,0') }} kr</div></td>
              <td>
                <span :class="['up-badge', 'up-badge--' + item.status]">{{ statusLabel(item.status) }}</span>
              </td>
              <td><div class="up-cell-val">{{ item.date | date }}</div></td>
            </tr>
          </tbody>
          </table>
        </div>

        <div v-if="!loading && transactions.length === 0" class="up-empty">
          <i class="fal fa-inbox up-empty-icon"></i>
          <p class="up-empty-text">No upfront payments yet</p>
          <v-btn color="#e05540" dark small @click="dialog = true">Create your first</v-btn>
        </div>

        <div v-if="total > tableOptions.itemsPerPage" class="up-pagination">
          <v-pagination
            v-model="tableOptions.page"
            :length="Math.ceil(total / tableOptions.itemsPerPage)"
            :total-visible="7"
            color="#e05540"
          ></v-pagination>
        </div>
      </div>

      <upfront-payment-dialog v-model="dialog" @created="loadHistory" />
    </div>
  </subscription-guard>
</template>

<script>
import { mapState, mapActions } from "vuex";
import SubscriptionGuard from "@/components/business/pages/SubscriptionGuard.vue";
import UpfrontPaymentDialog from "@/components/common/dialogs/UpfrontPaymentDialog.vue";

const STATUS_LABELS = { 1: "Pending", 2: "Approved", 3: "Invoiced", 4: "Paid" };
const AVATAR_COLORS = ["#4caf50", "#2196f3", "#e91e63", "#9c27b0", "#ff9800", "#00bcd4", "#795548", "#607d8b"];

export default {
  name: "UpfrontPayment",
  components: { UpfrontPaymentDialog, SubscriptionGuard },
  data() {
    return {
      dialog: false,
      creatingCampaign: false,
      loading: false,
      transactions: [],
      total: 0,
      tableOptions: { page: 1, itemsPerPage: 10 },
    };
  },
  computed: {
    ...mapState("core/auth", ["user"]),
  },
  watch: {
    'tableOptions.page'() { this.loadHistory(); },
  },
  created() {
    this.loadHistory();
  },
  methods: {
    ...mapActions("core/campaigns", ["createCampaign"]),
    ...mapActions("business", ["getTransactionsReport"]),
    newCampaign() {
      this.creatingCampaign = true;
      this.createCampaign({ business_uuid: this.user.business.uuid, country_id: 61 })
        .then(campaign => {
          this.$router.push({ name: "campaign.create", params: { id: campaign.uuid } });
        })
        .finally(() => { this.creatingCampaign = false; });
    },
    loadHistory() {
      this.loading = true;
      this.getTransactionsReport({
        type: 5,
        page: this.tableOptions.page,
        limit: this.tableOptions.itemsPerPage,
      }).then(res => {
        this.transactions = res.data;
        this.total = res.total;
      }).finally(() => { this.loading = false; });
    },
    statusLabel(status) { return STATUS_LABELS[status] || status; },
    avatarInitials(item) {
      const name = item.influencer?.user?.full_name || '';
      return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
    },
    avatarColor(item) {
      const name = item.influencer?.user?.full_name || '';
      return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
    },
  },
};
</script>

<style lang="scss" scoped>
$accent: #e05540;

.up-wrap {
  max-width: 1100px;
  margin: 0 auto;
  padding-bottom: 40px;
}

.up-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.up-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 4px;
}

.up-subtitle {
  font-size: 13px;
  color: #888;
  margin: 0;
}

.up-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.up-vat-note {
  font-size: 12px;
  color: #aaa;
}

.up-btn-secondary {
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-color: #ddd !important;
  color: #333 !important;
  border-radius: 8px !important;
  background: #fff !important;
  height: 30px !important;
  padding: 0 12px !important;

  &:hover {
    background: #f5f5f5 !important;
  }
}

.up-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 24px;

  @media (max-width: 800px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
}

.up-info-card {
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 12px;
  padding: 20px;
}

.up-info-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fff0ee;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: $accent;
  margin-bottom: 12px;
}

.up-info-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 6px;
}

.up-info-text {
  font-size: 13px;
  color: #777;
  line-height: 1.6;
}

.up-table-card {
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 12px;
  overflow: hidden;
}

.up-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.up-table-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
}

.up-loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.up-table {
  width: 100%;
  border-collapse: collapse;

  thead tr { border-bottom: 1px solid #f0f0f0; }

  th {
    font-size: 11px;
    font-weight: 600;
    color: #aaa;
    letter-spacing: 0.05em;
    text-align: left;
    padding: 12px 20px;
  }

  td { padding: 14px 20px; vertical-align: middle; }
}

.up-row {
  border-bottom: 1px solid #f8f8f8;
  &:last-child { border-bottom: none; }
  &:hover { background: #fafafa; }
}

.up-inf-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.up-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.up-inf-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
}

.up-inf-sub {
  font-size: 12px;
  color: #aaa;
  margin-top: 1px;
}

.up-cell-val { font-size: 14px; color: #555; }

.up-amount {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
}

.up-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;

  &--1 { background: #fff8e1; color: #f57f17; }
  &--2 { background: #e3f2fd; color: #1565c0; }
  &--3 { background: #fff0ee; color: $accent; }
  &--4 { background: #e8f5e9; color: #2e7d32; }
}

.up-empty {
  text-align: center;
  padding: 56px 20px;

  .up-empty-icon { font-size: 36px; color: #ddd; display: block; margin-bottom: 12px; }
  .up-empty-text { font-size: 14px; color: #aaa; margin-bottom: 16px; }
}

.up-pagination {
  display: flex;
  justify-content: center;
  padding: 16px;
  border-top: 1px solid #f0f0f0;
}

.up-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
</style>